/*
 *   File : mark-as-paid.jsx
 *   Author : https://evoqins.com
 *   Description : Confirm payout / mark as paid modal
 *   Integrations : revenue-payout/mark-as-paid
 *   Version : 1.1.0
 */

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { PrimaryButton } from "../Buttons";
import { Input } from "../FormElements";
import Icon from "../icons";
import datePickerStyles from "../../Style/Components/date-picker.module.scss";

const ModalOverlay = ({ onClose }) => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-25"
      style={{ zIndex: 1040 }}
      onClick={onClose}
    />
  );
};

function formatDisplayDate(date) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatApiDate(date) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null;
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const y = date.getFullYear();
  return `${d}-${m}-${y}`;
}

function SummaryField({ label, value }) {
  return (
    <div className="d-flex flex-column gap-2">
      <span className="fw-400 fs-12 lh-16 text-davys-gray">{label}</span>
      <span className="fw-500 fs-16 lh-20 text-eerie-black">{value || "-"}</span>
    </div>
  );
}

export default function MarkAsPaidModal({
  open = false,
  payout = null,
  loading = false,
  onCancel,
  onConfirm,
}) {
  const [transactionId, setTransactionId] = useState("");
  const [payoutDate, setPayoutDate] = useState(null);
  const [transactionError, setTransactionError] = useState("");
  const [dateError, setDateError] = useState("");
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setTransactionId("");
      setPayoutDate(null);
      setTransactionError("");
      setDateError("");
      setCalendarOpen(false);
    }
  }, [open]);

  const validate = () => {
    let valid = true;
    if (!transactionId.trim()) {
      setTransactionError("Transaction ID is required");
      valid = false;
    } else {
      setTransactionError("");
    }
    if (!payoutDate) {
      setDateError("Payout date is required");
      valid = false;
    } else {
      setDateError("");
    }
    return valid;
  };

  const handleMarkAsPaid = () => {
    if (!validate()) return;
    onConfirm?.({
      transaction_id: transactionId.trim(),
      payout_date: formatApiDate(payoutDate),
      payout,
    });
  };

  if (!open) return null;

  return createPortal(
    <>
      <ModalOverlay onClose={onCancel} />

      <div
        className="position-fixed top-50 start-50 translate-middle e-modal-portal e-bg-blur-20 shadow p-0"
        style={{
          zIndex: 1055,
          width: "600px",
          background: "rgba(255, 255, 255, 0.8)",
          borderRadius: "28px",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="d-flex flex-column row-gap-3 justify-content-start align-items-start p-24">
          <div className="d-flex justify-content-between align-items-center w-100">
            <h5 className="fw-500 fs-18 lh-24 text-eerie-black mb-0">
              Confirm Payout
            </h5>
            <PrimaryButton
              icon="close"
              size={16}
              ClassName="bg-transparent p-0"
              onPress={onCancel}
            />
          </div>

          {/* Summary card — solid white, 16px radius */}
          <div
            className="w-100 p-3"
            style={{ background: "#FFFFFF", borderRadius: "16px" }}
          >
            <div className="row rg-24">
              <div className="col-4">
                <SummaryField label="Partner" value={payout?.partner} />
              </div>
              <div className="col-4">
                <SummaryField
                  label="Payout Period"
                  value={payout?.payout_period_full || payout?.payout_period}
                />
              </div>
              <div className="col-4">
                <SummaryField
                  label="Gross Commission"
                  value={
                    payout?.gross_commission_amount || payout?.gross_commission
                  }
                />
              </div>
              <div className="col-4">
                <SummaryField
                  label="TDS Deducted"
                  value={payout?.tds_amount || payout?.tds}
                />
              </div>
              <div className="col-4">
                <SummaryField label="Net Payout" value={payout?.net_payout} />
              </div>
            </div>
          </div>

          <div className="w-100">
            <Input
              label="Transaction ID"
              require
              labelStyle="fw-400 fs-14 lh-20 text-eerie-black mb-0 pb-2"
              placeholder="Enter transaction / UTR ID"
              value={transactionId}
              handleChange={(val) => {
                setTransactionId(val);
                setTransactionError("");
              }}
              error={transactionError}
              className="w-100 px-3 py-10 rounded-12"
            />
          </div>

          <div className="w-100">
            <p className="mb-0 fw-400 fs-14 lh-20 text-eerie-black pb-2">
              Payout Date<span className="text-cg-red">*</span>
            </p>
            <button
              type="button"
              className={`w-100 d-flex align-items-center justify-content-between e-input e-border-input rounded-12 px-3 py-10 bg-white text-start ${
                dateError ? "e-border-error" : ""
              }`}
              onClick={() => setCalendarOpen(true)}
            >
              <span
                className={`fw-400 fs-14 ${
                  payoutDate ? "text-eerie-black" : "text-granite-gray"
                }`}
              >
                {payoutDate ? formatDisplayDate(payoutDate) : "Enter date"}
              </span>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <rect
                  x="3"
                  y="5"
                  width="18"
                  height="16"
                  rx="2"
                  stroke="#6B7280"
                  strokeWidth="1.5"
                />
                <path d="M3 10H21" stroke="#6B7280" strokeWidth="1.5" />
                <path
                  d="M8 3V7"
                  stroke="#6B7280"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <path
                  d="M16 3V7"
                  stroke="#6B7280"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            {dateError && (
              <p className="mb-0 fs-12 text-cg-red pt-1">{dateError}</p>
            )}
          </div>

          <div className="row w-100 pt-2">
            <div className="col-6">
              <PrimaryButton
                name="Cancel"
                ClassName="rounded-pill bg-transparent border-oxford-blue lh-24 py-3 text-oxford-blue w-100 fs-16 fw-500"
                onPress={onCancel}
                disabled={loading}
              />
            </div>
            <div className="col-6">
              <PrimaryButton
                name={loading ? "Marking..." : "Mark as paid"}
                ClassName="rounded-pill bg-oxford-blue lh-24 py-3 text-white w-100 fs-16 fw-500"
                loading={loading}
                disabled={loading}
                onPress={handleMarkAsPaid}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Centered calendar overlay (does not change CustomDatePicker) */}
      {calendarOpen && (
        <>
          <div
            className="position-fixed top-0 start-0 w-100 h-100"
            style={{ zIndex: 1060, background: "transparent" }}
            onClick={() => setCalendarOpen(false)}
          />
          <div
            className="position-fixed top-50 start-50 translate-middle shadow"
            style={{
              zIndex: 1065,
              background: "#FFFFFF",
              borderRadius: "16px",
              padding: 16,
              minWidth: 320,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Calendar
              value={payoutDate}
              onChange={(value) => {
                setPayoutDate(value);
                setDateError("");
                setCalendarOpen(false);
              }}
              maxDate={new Date()}
              nextLabel={<Icon icon="arrow-right" size="16px" />}
              next2Label={null}
              prevLabel={<Icon icon="arrow-left" size="16px" />}
              prev2Label={null}
              showNeighboringMonth={true}
              locale="en-IN"
              className={`border-0 e-primary-font-poppins ${datePickerStyles.cdp_calendar}`}
            />
          </div>
        </>
      )}
    </>,
    document.body,
  );
}
